/**
 * Envio de erros não tratados do frontend para a Triagem (POST /client-errors).
 * Limites no cliente: no máximo MAX_REPORTS_PER_SESSION por sessão e um envio
 * por fingerprint, para não inundar a Central de Ocorrências.
 */
import { API_URL } from './api';
import { recordRecentError } from './recentErrors';

export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION || 'dev';

const MAX_REPORTS_PER_SESSION = 10;
const MAX_MESSAGE_LENGTH = 1000;
const MAX_STACK_LENGTH = 8000;

const sentFingerprints = new Set<string>();
let reportCount = 0;

function buildFingerprint(message: string, stack?: string): string {
  const firstFrame = stack?.split('\n').map((line) => line.trim()).find((line) => line.startsWith('at ') || line.includes('@')) ?? '';
  return `${message}|${firstFrame}`;
}

function normalizeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: error.message || error.name, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

/** Ruído de extensões e scripts de terceiros que não indicam bug do app */
function isIgnorable(message: string): boolean {
  return (
    message.includes('ResizeObserver loop') ||
    message === 'Script error.' ||
    message.includes('Failed to fetch dynamically imported module')
  );
}

/**
 * Envia o erro e resolve com o id da ocorrência criada (ou null se não enviado),
 * para que o relato do usuário possa ser vinculado a ele.
 */
export function reportClientError(error: unknown): Promise<string | null> {
  const { message, stack } = normalizeError(error);
  if (!message || isIgnorable(message)) return Promise.resolve(null);
  recordRecentError({ mensagem: message, origem: 'cliente' });
  if (reportCount >= MAX_REPORTS_PER_SESSION) return Promise.resolve(null);

  const fingerprint = buildFingerprint(message, stack);
  if (sentFingerprints.has(fingerprint)) return Promise.resolve(null);
  sentFingerprints.add(fingerprint);
  reportCount += 1;

  const body = JSON.stringify({
    message: message.slice(0, MAX_MESSAGE_LENGTH),
    stack: stack?.slice(0, MAX_STACK_LENGTH),
    // Sem query string/fragmento: podem carregar tokens ou dados pessoais
    url: `${window.location.origin}${window.location.pathname}`,
    release: APP_VERSION,
    userAgent: navigator.userAgent.slice(0, 500),
  });

  // keepalive permite concluir o envio mesmo se a página estiver fechando.
  // Falha no relato não deve gerar novo relato (evita laço), por isso é ignorada.
  return fetch(`${API_URL}/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const json = (await response.json()) as { data?: { id?: unknown } };
      return typeof json.data?.id === 'string' ? json.data.id : null;
    })
    .catch(() => null);
}

let isInstalled = false;

export function installGlobalErrorReporting(): void {
  if (isInstalled || typeof window === 'undefined') return;
  isInstalled = true;

  window.addEventListener('error', (event) => {
    void reportClientError(event.error ?? event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    void reportClientError(event.reason);
  });
}
